"use client";

import { getPostHogEventContext } from "@/lib/posthog-browser";
import { POSTHOG_EVENT_CONTEXT_FORM_FIELD } from "@/lib/posthog-constants";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { ConfirmGatedForm } from "@/ui/components/confirm-gate";
import { useCart } from "@/ui/components/cart/cart-context";
import { usePdpVariantNavigation } from "./variant-navigation-context";
import { addPdpLineToCheckout } from "./add-to-cart-action";

type AddToCartFeedback = { success: boolean; reason?: "selection-changed" } | null;
const FeedbackContext = createContext<AddToCartFeedback>(null);

export function useAddToCartFeedback() {
	return useContext(FeedbackContext);
}

/** Keeps both PDP and card feedback tied to the actual Saleor mutation result. */
export function AddToCartForm({
	children,
	id,
	className,
}: {
	children: ReactNode;
	id?: string;
	className?: string;
}) {
	const [feedback, setFeedback] = useState<AddToCartFeedback>(null);
	const navigation = usePdpVariantNavigation();
	const submitting = useRef(false);
	const { openCart } = useCart();
	const t = useTranslations("product");

	useEffect(() => {
		if (!feedback?.success) return;
		const timer = setTimeout(() => setFeedback(null), 1500);
		return () => clearTimeout(timer);
	}, [feedback]);

	async function submit(formData: FormData) {
		const variantId = String(formData.get("variantId") ?? "");
		if (navigation && !navigation.canSubmitVariant(variantId)) {
			setFeedback({ success: false, reason: "selection-changed" });
			return;
		}
		if (submitting.current) return;
		submitting.current = true;
		setFeedback(null);
		try {
			formData.set(POSTHOG_EVENT_CONTEXT_FORM_FIELD, JSON.stringify(getPostHogEventContext()));
			const result = await addPdpLineToCheckout(formData);
			setFeedback(result);
			if (result.success) openCart();
		} catch {
			setFeedback({ success: false });
		} finally {
			submitting.current = false;
		}
	}

	return (
		<FeedbackContext.Provider value={feedback}>
			<ConfirmGatedForm
				action={submit}
				id={id}
				className={className}
				// Keep the chosen strength and quantity after React completes the action.
				onReset={(event) => event.preventDefault()}
			>
				{children}
				{feedback?.success === false && (
					<p role="alert" className="mt-2 text-sm text-destructive">
						{t(feedback.reason === "selection-changed" ? "selectionChanged" : "addToCartError")}
					</p>
				)}
			</ConfirmGatedForm>
		</FeedbackContext.Provider>
	);
}
